import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ShieldCheck, MessageSquare, Send as SendIcon } from "lucide-react";
import SignatureCapture from "../components/esign/SignatureCapture";
import api, { formatApiError, API_BASE } from "../lib/api";

export default function EsignSigner() {
  const { envelopeId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentSent, setCommentSent] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing signing token."); setLoading(false); return; }
    api.get(`/esign/sign/${envelopeId}`, { params: { t: token } })
      .then((r) => setEnvelope(r.data.envelope))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [envelopeId, token]);

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/esign/sign/${envelopeId}?t=${encodeURIComponent(token)}`, payload);
      setEnvelope({ ...envelope, party: { ...envelope.party, status: "signed" }, status: r.data.envelope.status });
      toast.success("Signature recorded. Thank you!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const postComment = async () => {
    if (!commentText.trim()) { toast.error("Please describe your objection or change request."); return; }
    setCommentBusy(true);
    try {
      await api.post(`/esign/sign/${envelopeId}/comment?t=${encodeURIComponent(token)}`,
                       { body: commentText.trim() });
      toast.success("Your comment has been sent to the sender.");
      setCommentSent(true);
      setCommentText("");
      setShowComment(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommentBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-muted">Loading envelope…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md border border-white/10 bg-white/80 backdrop-blur p-6 text-center" data-testid="esign-error">
        <div className="overline mb-2">Signing Unavailable</div>
        <div className="text-ink text-sm">{error}</div>
      </div>
    </div>
  );

  const signed = envelope?.party?.status === "signed";
  const pdfUrl = `${API_BASE}/esign/pdf/${envelopeId}?t=${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-ink-surface/40" data-testid="esign-signer">
      <Toaster richColors position="top-right" />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-2">
          <div className="brand-mark text-xl leading-none"><span className="word">VENTURAZ</span></div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Powered by CorpZo</div>
        </div>
        <h1 className="font-display text-3xl text-ink mb-1" data-testid="esign-title">{envelope.title}</h1>
        <div className="text-xs text-ink-muted font-mono uppercase tracking-widest mb-6 flex items-center gap-2 flex-wrap">
          {envelope.doc_number && (
            <span className="text-gold" data-testid="esign-signer-doc-number">{envelope.doc_number}</span>
          )}
          <span>{envelope.document_type} · From {envelope.initiator_email}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <div className="overline mb-2">Document</div>
            <iframe
              src={pdfUrl}
              title="Document"
              className="w-full h-[520px] border border-white/10 bg-white"
              data-testid="esign-pdf-frame"
            />
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="border border-white/10 bg-white/80 backdrop-blur p-4">
              <div className="overline mb-2">Signing as</div>
              <div className="text-ink font-heading text-lg" data-testid="esign-signer-name">{envelope.party.name}</div>
              <div className="text-xs text-ink-muted">{envelope.party.email}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-2 text-gold">Role: {envelope.party.role}</div>
            </div>

            {signed ? (
              <div className="border border-gold/40 bg-gold/[0.08] p-6 text-center" data-testid="esign-signed-panel">
                <CheckCircle2 className="mx-auto text-gold mb-3" size={36} />
                <div className="font-heading text-ink text-xl mb-1">Signed</div>
                <div className="text-sm text-ink-muted">
                  We&apos;ve recorded your signature. You&apos;ll receive a completion email once every party has signed.
                </div>
              </div>
            ) : envelope.viewer_can_sign === false ? (
              <div className="border border-amber-500/40 bg-amber-500/[0.08] p-6" data-testid="esign-viewer-mismatch">
                <div className="font-heading text-ink text-lg mb-1">You&apos;re viewing this signing page as a different user</div>
                <div className="text-sm text-ink-soft">
                  Only <span className="font-mono">{envelope.party.email}</span> can sign here.
                  Sign out (or open this link in a private window) and use the invitation delivered to that inbox.
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-3">
                  Read-only preview · no audit event recorded
                </div>
              </div>
            ) : (
              <SignatureCapture signerName={envelope.party.name} onSign={submit} submitting={submitting} />
            )}

            {!signed && (
              <div className="border border-white/10 bg-white/60 backdrop-blur p-4" data-testid="esign-comment-panel">
                {!showComment ? (
                  <button onClick={() => setShowComment(true)}
                            className="w-full flex items-center justify-between text-left"
                            data-testid="esign-request-changes-btn">
                    <div>
                      <div className="overline flex items-center gap-1"><MessageSquare size={12} /> Request Changes</div>
                      <div className="text-[11px] text-ink-muted mt-1">Have an objection or a redline request? Send a note to the sender before signing.</div>
                      {commentSent && <div className="text-[11px] text-gold mt-1">✓ Your feedback was sent.</div>}
                    </div>
                    <span className="text-xs font-mono uppercase tracking-widest text-gold hover:text-gold/70 shrink-0 ml-2">Open</span>
                  </button>
                ) : (
                  <div className="space-y-2">
                    <div className="overline flex items-center gap-1"><MessageSquare size={12} /> Request Changes</div>
                    <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={4}
                                placeholder="e.g. Please modify clause 7 — the non-solicit term should be 6 months, not 12."
                                className="w-full px-3 py-2 border border-ink-line/40 bg-white text-sm text-ink"
                                data-testid="esign-comment-textarea" />
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setShowComment(false); setCommentText(""); }}
                                className="text-xs font-mono uppercase tracking-widest text-ink-muted">Cancel</button>
                      <button onClick={postComment} disabled={commentBusy || !commentText.trim()}
                                className="bg-ink text-white text-xs font-mono uppercase tracking-widest px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                                data-testid="esign-comment-submit">
                        <SendIcon size={12} /> {commentBusy ? "Sending…" : "Send to sender"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border border-white/10 bg-white/60 backdrop-blur p-4">
              <div className="overline mb-2 flex items-center gap-1"><ShieldCheck size={12} /> All parties</div>
              <ul className="space-y-2 text-sm">
                {envelope.all_parties.map((p, i) => (
                  <li key={i} className="flex justify-between" data-testid={`esign-party-${i}`}>
                    <span className="text-ink">{p.name} <span className="text-[10px] text-ink-muted">({p.role})</span></span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest ${p.status === "signed" ? "text-gold" : "text-ink-muted"}`}>{p.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
